import React, { useState } from 'react';
import { Check, ArrowRight, AlertTriangle, BarChart2, PartyPopper, ExternalLink } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { DashboardView } from '../types';
import { DASHBOARD_VIEW_OPTIONS } from '../config/constants';

interface OnboardingPageProps {
  onComplete: (selectedView: DashboardView) => void;
  onCreateSheet: () => Promise<string>;
  userName?: string;
}

export const OnboardingPage: React.FC<OnboardingPageProps> = ({
  onComplete,
  onCreateSheet,
  userName = 'there',
}) => {
  const { darkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<DashboardView>('standard');
  const [createdSheetId, setCreatedSheetId] = useState<string | null>(null);

  const steps = [
    {
      num: 1,
      title: 'Welcome to TallyUp Finance!',
      desc: `Hi ${userName}! Let's set up your financial dashboard in 3 easy steps.`,
    },
    {
      num: 2,
      title: 'Creating Your Spreadsheet',
      desc: "We're creating a secure template in your Google Drive...",
    },
    {
      num: 3,
      title: 'All Set!',
      desc: "Your spreadsheet is ready.\nChoose how much detail you'd like on your dashboard.",
    },
    {
      num: 4,
      title: 'Ready to Go!',
      desc: "You're all set. You can change this anytime in Settings.",
    },
  ];

  const currentStep = steps[step - 1];

  const handleNext = async () => {
    if (step === 1 || (step === 2 && createError)) {
      setStep(2);
      setIsCreatingSheet(true);
      setCreateError(null);
      try {
        const newSheetId = await onCreateSheet();
        setCreatedSheetId(newSheetId);
        setIsCreatingSheet(false);
        setStep(3);
      } catch (err) {
        console.error('Failed to create sheet:', err);
        setIsCreatingSheet(false);
        setCreateError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      onComplete(selectedView);
    }
  };

  return (
    <div
      className={`min-h-[100dvh] ${
        darkMode
          ? 'bg-slate-950'
          : 'bg-[#f8f9ff]'
      } antialiased flex items-center justify-center px-4 py-8 md:p-6`}
    >
      <div
        className={`max-w-2xl w-full p-6 md:p-12 rounded-3xl backdrop-blur-xl ${
          darkMode ? 'bg-slate-800/70 backdrop-blur-3xl border border-slate-700/50' : 'bg-white/70 backdrop-blur-3xl border border-white/40'
        } shadow-2xl`}
      >
        {/* Progress Bar */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-6 md:mb-12">
          {steps.map((s) => (
            <div key={s.num} className="flex items-center">
              <div
                className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold transition-all text-sm md:text-base ${
                  step >= s.num
                    ? 'bg-violet-600 text-white'
                    : darkMode
                    ? 'bg-white/10 text-slate-400'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step > s.num ? <Check size={18} /> : s.num}
              </div>
              {s.num < steps.length && (
                <div
                  className={`w-8 md:w-16 h-1 mx-1 md:mx-2 transition-all ${
                    step > s.num
                      ? 'bg-violet-600'
                      : darkMode
                      ? 'bg-white/10'
                      : 'bg-slate-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="text-center mb-6 md:mb-12">
          <h2
            className={`text-2xl md:text-4xl font-bold mb-2 md:mb-4 ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            {currentStep.title}
          </h2>
          <p
            className={`text-base md:text-xl whitespace-pre-line ${
              darkMode ? 'text-slate-300' : 'text-slate-600'
            }`}
          >
            {currentStep.desc}
          </p>
        </div>

        {/* Step-Specific Content */}
        {step === 1 && (
          <div className="space-y-3 md:space-y-6 mb-6 md:mb-12">
            {[
              {
                title: 'Secure & Private',
                desc: 'Your data stays in YOUR Google Drive. We never see it.',
              },
              {
                title: 'Easy to Use',
                desc: 'Beautiful UI for daily use. Open the spreadsheet anytime.',
              },
              {
                title: 'Always in Control',
                desc: 'Edit in TallyUp Finance or directly in Google Sheets. You own it.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className={`flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-xl ${
                  darkMode ? 'bg-white/5' : 'bg-slate-50'
                }`}
              >
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-violet-600 text-white flex items-center justify-center flex-shrink-0">
                  <Check size={16} />
                </div>
                <div>
                  <h3
                    className={`font-bold mb-1 text-sm md:text-base ${
                      darkMode ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    {item.title}
                  </h3>
                  <p
                    className={`text-xs md:text-sm ${
                      darkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="text-center mb-6 md:mb-12">
            {createError ? (
              <>
                <div className="inline-block">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-red-500/20 flex items-center justify-center mb-4 md:mb-6">
                    <AlertTriangle size={48} className="text-red-500" />
                  </div>
                </div>
                <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {createError}
                </p>
              </>
            ) : (
              <>
                <div className="inline-block">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-violet-600/20 flex items-center justify-center mb-4 md:mb-6 animate-pulse">
                    <BarChart2 size={48} className="text-violet-500" />
                  </div>
                </div>
                <p
                  className={`text-base md:text-lg ${
                    darkMode ? 'text-slate-400' : 'text-slate-600'
                  }`}
                >
                  {isCreatingSheet
                    ? 'Creating your template...'
                    : 'Setting up your dashboard...'}
                </p>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="mb-6 md:mb-12">
            {createdSheetId && (
              <a
                href={`https://docs.google.com/spreadsheets/d/${createdSheetId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-between gap-3 p-4 mb-5 rounded-xl transition-colors ${
                  darkMode
                    ? 'bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/15'
                    : 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  <svg width={36} height={36} viewBox="0 0 87.3 78" className="flex-shrink-0">
                    <path d="M6.6 66.85L14.1 78H73.2L80.7 66.85z" fill="#0066DA" />
                    <path d="M43.65 25L29.2 0H0L14.4 25z" fill="#00AC47" />
                    <path d="M73.2 78L87.3 53H58.1L43.65 78z" fill="#EA4335" />
                    <path d="M29.2 0L14.4 25L43.65 78L58.1 53z" fill="#00832D" />
                    <path d="M43.65 25L58.1 53H87.3L73.2 25z" fill="#2684FC" />
                    <path d="M6.6 66.85L43.65 25L14.4 25z" fill="#FFBA00" />
                  </svg>
                  <div>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      Your spreadsheet is in your Google Drive
                    </p>
                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-emerald-400/80' : 'text-emerald-600'}`}>
                      Tap to view it. Your data lives here — TallyUp just reads from it.
                    </p>
                  </div>
                </div>
                <ExternalLink size={16} className={darkMode ? 'text-emerald-400 flex-shrink-0' : 'text-emerald-600 flex-shrink-0'} />
              </a>
            )}
            <div className="space-y-3">
            {DASHBOARD_VIEW_OPTIONS.map((option) => {
              const isActive = selectedView === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setSelectedView(option.value as DashboardView)}
                  className={`w-full text-left p-4 md:p-5 rounded-xl transition-all flex items-start gap-3 md:gap-4 ${
                    isActive
                      ? darkMode
                        ? 'bg-violet-500/20 border-2 border-violet-500/60'
                        : 'bg-violet-100 border-2 border-violet-400'
                      : darkMode
                        ? 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                        : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                  }`}
                >
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isActive
                      ? 'border-violet-500 bg-violet-600'
                      : darkMode ? 'border-slate-500' : 'border-slate-300'
                  }`}>
                    {isActive && <Check size={12} className="text-white" />}
                  </div>
                  <div>
                    <h3 className={`font-bold text-sm md:text-base ${
                      isActive
                        ? darkMode ? 'text-violet-300' : 'text-violet-700'
                        : darkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      {option.label}
                    </h3>
                    <p className={`text-xs md:text-sm mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {option.description}
                    </p>
                  </div>
                </button>
              );
            })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mb-6 md:mb-12">
            <div
              className={`p-4 md:p-6 rounded-2xl ${
                darkMode
                  ? 'bg-green-500/20 border border-green-500/50'
                  : 'bg-green-50 border border-green-200'
              } text-center`}
            >
              <div className="mb-3 md:mb-4"><PartyPopper size={48} className="mx-auto text-green-500" /></div>
              <h3
                className={`text-xl md:text-2xl font-bold mb-2 ${
                  darkMode ? 'text-green-300' : 'text-green-700'
                }`}
              >
                Success!
              </h3>
              <p
                className={`text-sm md:text-base ${
                  darkMode ? 'text-green-200' : 'text-green-600'
                }`}
              >
                Your TallyUp Finance spreadsheet has been created in your Google Drive.
              </p>
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleNext}
          disabled={step === 2 && !createError}
          className={`w-full py-3 md:py-4 px-6 md:px-8 rounded-xl font-bold text-base md:text-lg transition-all flex items-center justify-center gap-2 ${
            step === 2 && !createError
              ? 'bg-slate-400 text-slate-200 cursor-not-allowed'
              : 'bg-violet-600 hover:bg-violet-700 text-white shadow-xl hover:shadow-2xl'
          }`}
        >
          {step === 1 && 'Get Started'}
          {step === 2 && (createError ? 'Try Again' : 'Creating...')}
          {step === 3 && 'Continue'}
          {step === 4 && 'Open Dashboard'}
          {(step !== 2 || createError) && <ArrowRight size={20} />}
        </button>

        {step === 4 && (
          <p
            className={`text-center mt-3 md:mt-4 text-xs md:text-sm ${
              darkMode ? 'text-slate-400' : 'text-slate-600'
            }`}
          >
            You can view your spreadsheet anytime in Google Drive
          </p>
        )}
      </div>
    </div>
  );
};
